import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  ANDROID_APP_PACKAGE,
  APP_STORE_URL,
  PLAY_STORE_URL,
  detectPlatform,
} from "../GiveawayRedirect/GiveawayRedirect";
import {
  buildCanonicalAppUrl,
  setAppleSmartBanner,
} from "../../utils/appLinkFallback";
import "./AppLinkRedirect.scss";

const getCurrentPlatform = () =>
  detectPlatform(
    window.navigator.userAgent,
    window.navigator.platform,
    window.navigator.maxTouchPoints,
  );

const getLinkContent = (pathname) => {
  if (pathname.startsWith("/dealsforyou/")) {
    return {
      eyebrow: "Teleport Now Deals For You",
      title: "Open this travel deal in Teleport Now",
      copy: "View the included travel essentials, latest price and availability in the Teleport Now app.",
    };
  }

  if (pathname.startsWith("/moments/")) {
    return {
      eyebrow: "Teleport Now Moments",
      title: "Open this Moment in Teleport Now",
      copy: "See the full Moment and continue exploring in the Teleport Now app.",
    };
  }

  if (/^\/esim\/[^/]+\/plans\/[^/]+/.test(pathname)) {
    return {
      eyebrow: "Teleport Now eSIM",
      title: "Open this eSIM plan in Teleport Now",
      copy: "Check the latest price and availability securely in the Teleport Now app.",
    };
  }

  if (pathname.startsWith("/esim/")) {
    return {
      eyebrow: "Teleport Now eSIM",
      title: "Explore these eSIM plans in Teleport Now",
      copy: "Compare the latest plans, prices and availability in the Teleport Now app.",
    };
  }

  return {
    eyebrow: "Teleport Now Airport Lounge",
    title: "Open this lounge in Teleport Now",
    copy: "Check the latest lounge access and availability in the Teleport Now app.",
  };
};

export const buildAppLaunchUrl = (platform, publicUrl) => {
  if (platform !== "android") return publicUrl;

  const url = new URL(publicUrl);
  return (
    `intent://${url.host}${url.pathname}${url.search}` +
    `#Intent;scheme=https;package=${ANDROID_APP_PACKAGE};` +
    "action=android.intent.action.VIEW;" +
    "category=android.intent.category.BROWSABLE;" +
    `S.browser_fallback_url=${encodeURIComponent(PLAY_STORE_URL)};end`
  );
};

const AppLinkRedirect = () => {
  const location = useLocation();
  const platform = useMemo(getCurrentPlatform, []);
  const canonicalUrl = buildCanonicalAppUrl(location.pathname, location.search);
  const content = getLinkContent(location.pathname);
  const appLaunchUrl = buildAppLaunchUrl(platform, canonicalUrl);

  useEffect(() => {
    document.title = content.title;
    setAppleSmartBanner(canonicalUrl);
  }, [canonicalUrl, content.title]);

  const storeUrl = platform === "android" ? PLAY_STORE_URL : APP_STORE_URL;
  const isDesktop = platform === "desktop";

  return (
    <main className="app-link-redirect-page">
      <section className="app-link-redirect-card">
        <img
          className="app-link-redirect-logo"
          src="/images/teleport-now-color-logo.png"
          alt="Teleport Now"
        />
        <div className="app-link-redirect-icon" aria-hidden="true">✈</div>
        <p className="app-link-redirect-eyebrow">{content.eyebrow}</p>
        <h1>{content.title}</h1>
        <p className="app-link-redirect-copy">{content.copy}</p>

        {platform === "android" && (
          <a className="app-link-open-button" href={appLaunchUrl}>
            Open Teleport Now
          </a>
        )}

        <a
          className="app-link-store-link"
          href={storeUrl}
          rel="noopener noreferrer"
        >
          <img
            src={platform === "android"
              ? "/images/icons/googleplay.svg"
              : "/images/icons/appstore.svg"}
            alt={platform === "android"
              ? "Get it on Google Play"
              : "Download on the App Store"}
          />
        </a>

        <p className="app-link-redirect-help">
          {platform === "ios"
            ? "If this page stays open, tap OPEN in the App Store banner above."
            : isDesktop
              ? "Open this link on your phone after installing Teleport Now."
              : "If the app is not installed, download it and return to this link."}
        </p>
      </section>
    </main>
  );
};

export default AppLinkRedirect;
